import { useState, useCallback, useMemo } from 'react';
import { createAddressString } from '@utils/address';
import { DEFAULT_MAP_CENTER } from '@constants/geo';
import type { storeAddressT } from '@store/UserStore.type';
import type { UserStoreType } from '@store/UserStore';
import { useStores } from '@root/src/stores';

export interface BalloonState {
  address?: string;
  dadataId?: string;
  map?: string | null;
}

export interface AddressFormFields {
  fio: string;
  phone: string;
  email: string;
  address: string;
  flat: string;
  comment: string;
  dadataId: number | null;
  coords: number[];
  balloonState: BalloonState;
  isMainAddress: boolean;
  isAddingNewAddress: boolean;
  isShowMapMarkPlace: boolean;
  selectedAddress: number | null;
  isAddAddress: boolean;
}

export interface AddressFormReturn {
  fields: AddressFormFields;
  setField: <K extends keyof AddressFormFields>(
    field: K,
    value: AddressFormFields[K]
  ) => void;
  setFields: (partial: Partial<AddressFormFields>) => void;
  resetFields: (defaults?: Partial<AddressFormFields>) => void;
  editAddress: (address: storeAddressT) => void;
  prepareNewAddress: (
    user: Pick<NonNullable<UserStoreType['user']>, 'fio' | 'phone' | 'email'>
  ) => void;
  selectMainAddress: () => void;
}

const DEFAULT_COORDS = DEFAULT_MAP_CENTER;

const defaultFields: AddressFormFields = {
  fio: '',
  phone: '',
  email: '',
  address: '',
  flat: '',
  comment: '',
  dadataId: null,
  coords: DEFAULT_COORDS,
  balloonState: {},
  isMainAddress: false,
  isAddingNewAddress: false,
  isShowMapMarkPlace: false,
  selectedAddress: null,
  isAddAddress: false
};

export const useAddressForm = (
  initialValues?: Partial<AddressFormFields>
): AddressFormReturn => {
  const { cartStore } = useStores();
  const { regionData } = cartStore;

  const geoCenterKey = regionData?.fields?.geo_center?.join(',');
  const defaultCenter = useMemo(
    () => regionData?.fields?.geo_center || DEFAULT_COORDS,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [geoCenterKey]
  );

  const [fields, setFieldsState] = useState<AddressFormFields>({
    ...defaultFields,
    coords: defaultCenter,
    ...initialValues
  });

  const setField = useCallback(
    <K extends keyof AddressFormFields>(
      field: K,
      value: AddressFormFields[K]
    ) => {
      setFieldsState(prev => ({ ...prev, [field]: value }));
    },
    []
  );

  const setFields = useCallback((partial: Partial<AddressFormFields>) => {
    setFieldsState(prev => ({ ...prev, ...partial }));
  }, []);

  const resetFields = useCallback((defaults?: Partial<AddressFormFields>) => {
    setFieldsState({ ...defaultFields, ...defaults });
  }, []);

  const editAddress = useCallback(
    (address: storeAddressT) => {
      const newFields: AddressFormFields = {
        ...defaultFields,
        isAddAddress: true,
        selectedAddress: address.id ?? null,
        fio: address.fio || '',
        phone: address.phone || '',
        email: (address as storeAddressT & { email?: string }).email || '',
        address: createAddressString(address),
        flat: address.flat || '',
        dadataId: address.dadata?.id ?? null,
        coords:
          address.dadata?.geoLat && address.dadata?.geoLon
            ? [Number(address.dadata.geoLat), Number(address.dadata.geoLon)]
            : defaultCenter,
        comment: address.comment || '',
        isMainAddress: !!address.primary
      };
      setFieldsState(newFields);
    },
    [defaultCenter]
  );

  const prepareNewAddress = useCallback(
    (
      user: Pick<NonNullable<UserStoreType['user']>, 'fio' | 'phone' | 'email'>
    ) => {
      setFieldsState(prev => ({
        ...prev,
        selectedAddress: null,
        isAddAddress: true,
        isAddingNewAddress: true,
        dadataId: null,
        fio: user?.fio || '',
        phone: user?.phone || '',
        email: user?.email || '',
        address: '',
        flat: '',
        coords: defaultCenter,
        comment: ''
      }));
    },
    [defaultCenter]
  );

  const selectMainAddress = useCallback(() => {
    setFieldsState(prev => ({
      ...prev,
      isMainAddress: !prev.isMainAddress
    }));
  }, []);

  return {
    fields,
    setField,
    setFields,
    resetFields,
    editAddress,
    prepareNewAddress,
    selectMainAddress
  };
};

export default useAddressForm;
